import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSubmitPartnerInquiry } from '../../features/partners/hooks/usePartnersPage';
import { useRecaptcha } from '../../features/partners/hooks/useRecaptcha';
import { inquiryFormSchema, InquiryFormData, BUSINESS_TYPES } from './inquiryFormSchema';
import { FormInput } from './FormInput';
import { FormSelect } from './FormSelect';
import { FormTextarea } from './FormTextarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const INQUIRY_FORM_DEBUG = import.meta.env.MODE !== 'test';

export const InquiryForm = () => {
    const { register, handleSubmit, formState: { errors }, reset } = useForm<InquiryFormData>({
        resolver: zodResolver(inquiryFormSchema),
        defaultValues: {
            companyName: '',
            contactName: '',
            email: '',
            phoneNumber: '',
            businessType: '',
            message: ''
        }
    });

    const submitMutation = useSubmitPartnerInquiry();
    const { executeRecaptcha } = useRecaptcha();
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const onSubmit = async (data: InquiryFormData) => {
        setSubmitError(null);
        try {
            const recaptchaToken = await executeRecaptcha('partner_inquiry');

            await submitMutation.mutateAsync({
                ...data,
                recaptchaToken
            });

            setSubmitSuccess(true);
            reset();
        } catch (error) {
            if (INQUIRY_FORM_DEBUG) {
                console.error('Failed to submit inquiry:', error);
            }
            setSubmitError('Something went wrong and we couldn\'t send your message. Please try again, or contact us directly if the problem continues.');
        }
    };

    if (submitSuccess) {
        return (
            <div className="rounded-3xl border border-success-border bg-success-subtle p-8 text-center animate-fade-in">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                    <svg className="h-8 w-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="mb-2 text-2xl font-bold text-foreground">Message Sent!</h3>
                <p className="mb-6 text-muted-foreground">
                    Thank you for your interest in partnering with Stork Rentals. Our team will review your inquiry and get back to you shortly.
                </p>
                <Button
                    onClick={() => setSubmitSuccess(false)}
                    type="button"
                    variant="link"
                    className="h-auto px-0 text-primary"
                >
                    Send another message
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                    id="companyName"
                    label="Company Name"
                    {...register('companyName')}
                    error={errors.companyName?.message}
                    required
                    placeholder="e.g. Grand Hotel Resort"
                />

                <FormInput
                    id="contactName"
                    label="Contact Name"
                    {...register('contactName')}
                    error={errors.contactName?.message}
                    required
                    placeholder="Your full name"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                    id="email"
                    label="Email Address"
                    type="email"
                    {...register('email')}
                    error={errors.email?.message}
                    required
                    placeholder="you@company.com"
                />

                <FormInput
                    id="phoneNumber"
                    label="Phone Number"
                    type="tel"
                    {...register('phoneNumber')}
                    error={errors.phoneNumber?.message}
                    placeholder="+1 (555) 000-0000"
                />
            </div>

            <FormSelect
                id="businessType"
                label="Business Type"
                options={BUSINESS_TYPES}
                {...register('businessType')}
                error={errors.businessType?.message}
                placeholder="Select your business type"
            />

            <FormTextarea
                id="message"
                label="Message / Inquiry"
                {...register('message')}
                error={errors.message?.message}
                rows={4}
                placeholder="Tell us about your business and how you'd like to partner..."
                helperText="Max 2000 characters"
            />

            {submitError && (
                <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive" role="alert">
                    {submitError}
                </div>
            )}

            <Button
                type="submit"
                disabled={submitMutation.isPending}
                className={cn(
                    'h-12 w-full rounded-xl text-base font-semibold shadow-card transition-transform duration-200 hover:-translate-y-0.5',
                    'disabled:translate-y-0'
                )}
            >
                {submitMutation.isPending ? (
                    <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                    </span>
                ) : (
                    'Submit Inquiry'
                )}
            </Button>

            <p className="mt-4 text-center text-xs text-muted-foreground">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" className="underline underline-offset-4 hover:text-foreground">Privacy Policy</a> and{' '}
                <a href="https://policies.google.com/terms" className="underline underline-offset-4 hover:text-foreground">Terms of Service</a> apply.
            </p>
        </form>
    );
};
